import React, { SetStateAction, useMemo } from 'react';
import type { Risk } from '../types/risk.type';

export default function RiskSummary({
    risks,
    onSelectCategory,
}: {
    risks: Risk[];
    onSelectCategory: React.Dispatch<SetStateAction<string | null>>;
}) {
    const isValidPI = (n: number | undefined | null) => typeof n === 'number' && n >= 1 && n <= 5;
    const parseISO = (d?: string | null) => {
        if (!d) return null;
        const s = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
        const t = Date.parse(s);
        return Number.isFinite(t) ? new Date(t) : null;
    };

    // 평균 지표
    const means = useMemo(() => {
        const valid = risks.filter((r) => isValidPI(r.probability) && isValidPI(r.impact));
        if (!valid.length) return { avgScore: 0, avgP: 0, avgI: 0 };
        const sumP = valid.reduce((s, r) => s + (r.probability as number), 0);
        const sumI = valid.reduce((s, r) => s + (r.impact as number), 0);
        const avgP = sumP / valid.length;
        const avgI = sumI / valid.length;
        return { avgScore: avgP * avgI, avgP, avgI };
    }, [risks]);

    // 일정
    const schedule = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const plus7 = new Date(today);
        plus7.setDate(today.getDate() + 7);
        let overdue = 0,
            due7 = 0,
            noDue = 0,
            review7 = 0;
        for (const r of risks) {
            if (!r.dueDate) noDue++;
            const due = parseISO(r.dueDate);
            if (due) {
                if (due < today) overdue++;
                else if (due <= plus7) due7++;
            }
            const next = parseISO((r as any).nextReviewAt);
            if (next && next <= plus7 && next >= today) review7++;
        }
        return { overdue, due7, noDue, review7 };
    }, [risks]);

    // 커버리지
    const coverage = useMemo(() => {
        const hasMit = risks.filter((r) => (r.mitigationPlan ?? '').trim()).length;
        const hasCon = risks.filter((r) => (r.contingencyPlan ?? '').trim()).length;
        const all = risks.length || 1;
        const pct = (n: number) => Math.round((n / all) * 100);
        return { mit: hasMit, con: hasCon, mitPct: pct(hasMit), conPct: pct(hasCon) };
    }, [risks]);

    // Top 5
    const topByCategory = useMemo(() => {
        const map = new Map<string, number>();
        risks.forEach((r) => {
            const k = (r.category || 'Uncategorized').trim();
            map.set(k, (map.get(k) || 0) + 1);
        });
        return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    }, [risks]);

    const topByOwner = useMemo(() => {
        const map = new Map<string, number>();
        risks.forEach((r) => {
            const k = (r.ownerUserId || '—').trim();
            map.set(k, (map.get(k) || 0) + 1);
        });
        return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    }, [risks]);

    // Open/Closed 통계
    const statusStats = useMemo(() => {
        const total = risks.length || 1;
        const closed = risks.filter((r) => (r.status ?? 'OPEN') === 'CLOSED').length;
        const open = total - closed;
        const closedPct = Math.round((closed / total) * 100);
        return { open, closed, closedPct };
    }, [risks]);

    return (
        <div className="grid grid-cols-3 gap-3 auto-rows-fr">
            {/* 1열 위: Averages */}
            <div className="rounded-lg border p-3 col-start-1 row-start-1">
                <div className="text-xs text-gray-500 mb-1">Averages</div>
                <div className="text-sm">
                    Score&nbsp;<b>{means.avgScore ? means.avgScore.toFixed(1) : '0.0'}</b>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                    P&nbsp;{means.avgP.toFixed(1)} · I&nbsp;{means.avgI.toFixed(1)}
                </div>
            </div>

            {/* 1열 아래: Schedule */}
            <div className="rounded-lg border p-3 col-start-1 row-start-2">
                <div className="text-xs text-gray-500 mb-1">Schedule</div>
                <div className="text-sm">
                    Overdue&nbsp;<b>{schedule.overdue}</b>
                </div>
                <div className="text-sm">
                    Due ≤ 7d&nbsp;<b>{schedule.due7}</b>
                </div>
                <div className="text-xs text-gray-500">No Due&nbsp;{schedule.noDue}</div>
                <div className="text-xs text-gray-500">Next Review ≤ 7d&nbsp;{schedule.review7}</div>
            </div>

            {/* 2열 위: Coverage */}
            <div className="rounded-lg border p-3 col-start-2 row-start-1">
                <div className="text-xs text-gray-500 mb-1">Coverage</div>
                <div className="text-sm">
                    Mitigation&nbsp;<b>{coverage.mit}</b>
                    <span className="text-xs text-gray-500">&nbsp;({coverage.mitPct}%)</span>
                </div>
                <div className="text-sm">
                    Contingency&nbsp;<b>{coverage.con}</b>
                    <span className="text-xs text-gray-500">&nbsp;({coverage.conPct}%)</span>
                </div>
            </div>

            {/* 2열 아래: 좌/우 분할 (좌=Status, 우=Top Owners) */}
            <div className="rounded-lg border col-start-2 row-start-2 h-full">
                {/* 카드 내부 패딩은 여기서 한 번만 */}
                <div className="p-3 grid grid-cols-2 items-stretch h-full divide-x divide-gray-200 gap-0">
                    {/* 왼쪽: 상태 통계 */}
                    <div className="pr-3 flex flex-col justify-between">
                        <div>
                            <div className="text-xs text-gray-500 mb-1">Status</div>
                            <div className="text-sm">
                                Open&nbsp;<b>{statusStats.open}</b>
                            </div>
                            <div className="text-sm">
                                Closed&nbsp;<b>{statusStats.closed}</b>
                            </div>
                            <div className="text-xs text-gray-500">Closed %&nbsp;{statusStats.closedPct}%</div>
                        </div>
                    </div>

                    {/* 오른쪽: Top Owners */}
                    <div className="pl-3">
                        <div className="text-xs text-gray-500 mb-1">Top Owners</div>
                        <ul className="text-sm space-y-1">
                            {topByOwner.length ? (
                                topByOwner.map(([k, v]) => (
                                    <li key={k} className="flex justify-between">
                                        <span className="truncate max-w-[160px]" title={k}>
                                            {k}
                                        </span>
                                        <b>{v}</b>
                                    </li>
                                ))
                            ) : (
                                <li className="text-xs text-gray-400">No data</li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* 3열 전체: Top Categories (두 줄 합침) */}
            <div className="rounded-lg border p-3 col-start-3 row-start-1 row-span-2 h-full">
                <div className="text-xs text-gray-500 mb-1">Top Categories</div>
                <ul className="space-y-1 text-sm">
                    {topByCategory.length ? (
                        topByCategory.map(([k, v]) => (
                            <li key={k} className="flex justify-between">
                                <button
                                    type="button"
                                    className="truncate max-w-[200px] text-left hover:text-gray-600"
                                    title={`Filter by ${k}`}
                                    onClick={() => onSelectCategory(k)}
                                >
                                    {k}
                                </button>
                                <b>{v}</b>
                            </li>
                        ))
                    ) : (
                        <li className="text-xs text-gray-400">No data</li>
                    )}
                </ul>
            </div>
        </div>
    );
}
